import { Navigate, Outlet } from 'react-router-dom';
import { Button } from '@react-apps/design-system';
import { loadTargetRoles } from '@/shared/impersonation/load-target-roles';
import {
  useEffectiveRoles,
  useTargetRolesStatus,
} from '@/shared/view-as/use-effective-roles';
import { hasAnyRole, type Role } from './roles';

interface RoleGuardProps {
  /** Papéis que liberam o acesso — basta ter **qualquer um** (admin geral é curinga). */
  roles: Role[];
}

/**
 * Guard de rota por papel, usado como layout route (`element`) sobre as rotas
 * que protege. Renderiza só se o usuário tiver algum dos papéis; senão redireciona
 * para a home.
 *
 * Componível por nível: envolva o módulo inteiro com um conjunto amplo e aninhe
 * um `RoleGuard` mais específico em sub-rotas que exigem menos papéis (ex.: módulo
 * = [admin, customer_success], detalhe = [admin]).
 *
 * É somente UX — esconde/redireciona no cliente. A autorização real é do backend
 * (`@Roles` no monolith) em toda rota protegida; mexer no localStorage não dá
 * acesso a dado nenhum.
 *
 * Assume que o usuário já está autenticado (fica aninhado sob AuthGuard).
 */
export function RoleGuard({ roles }: RoleGuardProps) {
  const userRoles = useEffectiveRoles();
  const targetRolesStatus = useTargetRolesStatus();

  // Sob máscara, roles do alvo chegando ou que falharam não são "nenhuma role":
  // redirecionar tiraria da tela quem recarregou a página mascarado.
  if (targetRolesStatus === 'pending') {
    return (
      <div role="status" className="p-6">
        <span className="sr-only">Carregando as permissões do usuário</span>
        <div
          aria-hidden="true"
          className="bg-(--color-bg-secondary) h-8 w-48 animate-pulse rounded-lg"
        />
      </div>
    );
  }
  if (targetRolesStatus === 'error') {
    return (
      <div
        role="alert"
        className="m-6 space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200"
      >
        <p>Não deu para carregar as permissões do usuário.</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void loadTargetRoles()}
        >
          Tentar de novo
        </Button>
      </div>
    );
  }

  if (!hasAnyRole(userRoles, roles)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
